// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// Utility functions for CSR assignment operations
// Date: July 26, 2025

export interface CSRWorkload {
  csr_id: string;
  csr_email: string;
  csr_name?: string;
  assigned_campaigns: number;
  assigned_companies?: number;
  company_campaigns?: number;
  active_lead_responses: number;
  total_workload_score: number;
  csr_joined_date?: string;
  csr_last_active?: string;
}

export interface EnhancedCSRWorkload extends CSRWorkload {
  assigned_companies: number;
  company_campaigns: number;
  company_workload_score: number; // companies * 5
  campaign_workload_score: number; // individual campaigns * 3
  lead_response_score: number; // lead_responses * 1
}

export interface AdminCampaign {
  id: string;
  name: string;
  user_id: string;
  user_email?: string;
  status: string;
  assigned_csr?: string | null;
  assigned_csr_email?: string;
  assignment_type?: 'company' | 'campaign' | 'unassigned';
  created_at: string;
  updated_at?: string;
  company_id?: string;
  company_name?: string;
}

export interface BulkAssignmentResult {
  success: boolean;
  updated_campaigns: number;
  error?: string;
}

/**
 * Calculate workload score based on companies, campaigns and lead responses
 * Enhanced Formula: (companies × 5) + (campaigns × 3) + lead_responses
 * Companies are weighted most heavily as they involve multiple campaigns
 * Individual campaigns require ongoing management (3x weight)
 * Lead responses require immediate attention (1x weight)
 */
export const calculateWorkloadScore = (campaigns: number, leadResponses: number): number => {
  return (campaigns * 3) + leadResponses;
};

/**
 * Calculate enhanced workload score with company assignments
 * Formula: (companies × 5) + (individual_campaigns × 3) + lead_responses
 */
export const calculateEnhancedWorkloadScore = (
  companies: number, 
  individualCampaigns: number, 
  leadResponses: number
): number => {
  return (companies * 5) + (individualCampaigns * 3) + leadResponses;
};

/**
 * Calculate workload breakdown for enhanced display
 */
export const calculateWorkloadBreakdown = (workload: EnhancedCSRWorkload) => {
  const companyScore = workload.assigned_companies * 5;
  const campaignScore = workload.assigned_campaigns * 3;
  const leadScore = workload.active_lead_responses * 1;
  
  return {
    company_workload_score: companyScore,
    campaign_workload_score: campaignScore,
    lead_response_score: leadScore,
    total_workload_score: companyScore + campaignScore + leadScore
  };
};

/**
 * Determine workload level based on total score
 * Thresholds: Light (≤5), Moderate (6-15), Heavy (>15)
 */
export type WorkloadLevel = 'light' | 'moderate' | 'heavy';

export const getWorkloadLevel = (score: number): WorkloadLevel => {
  if (score <= 5) return 'light';
  if (score <= 15) return 'moderate';
  return 'heavy';
};

/**
 * Get CSS classes for workload level indicators
 */
export const getWorkloadLevelStyles = (level: WorkloadLevel): string => {
  switch (level) {
    case 'light':
      return 'bg-green-100 text-green-800';
    case 'moderate':
      return 'bg-yellow-100 text-yellow-800';
    case 'heavy':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Sort CSRs by workload score (ascending - least busy first)
 */
export const sortCSRsByWorkload = (csrs: CSRWorkload[]): CSRWorkload[] => {
  return [...csrs].sort((a, b) => {
    // Primary sort: by workload score (ascending)
    if (a.total_workload_score !== b.total_workload_score) {
      return a.total_workload_score - b.total_workload_score;
    }
    // Secondary sort: by email (alphabetical)
    return a.csr_email.localeCompare(b.csr_email);
  });
};

/**
 * Filter campaigns based on assignment status
 */
export const filterCampaignsByAssignment = (
  campaigns: AdminCampaign[],
  filter: 'all' | 'assigned' | 'unassigned'
): AdminCampaign[] => {
  switch (filter) {
    case 'assigned':
      return campaigns.filter(campaign => campaign.assigned_csr);
    case 'unassigned':
      return campaigns.filter(campaign => !campaign.assigned_csr);
    case 'all':
    default:
      return campaigns;
  }
};

/**
 * Filter campaigns based on status
 */
export const filterCampaignsByStatus = (
  campaigns: AdminCampaign[],
  status: string
): AdminCampaign[] => {
  if (status === 'all') return campaigns;
  return campaigns.filter(campaign => campaign.status === status);
};

/**
 * Search campaigns by name or owner email
 */
export const searchCampaigns = (
  campaigns: AdminCampaign[],
  searchTerm: string
): AdminCampaign[] => {
  if (!searchTerm.trim()) return campaigns;
  
  const term = searchTerm.toLowerCase().trim();
  return campaigns.filter(campaign => 
    campaign.name.toLowerCase().includes(term) ||
    (campaign.user_email && campaign.user_email.toLowerCase().includes(term))
  );
};

/**
 * Get campaign status badge color classes
 */
export const getCampaignStatusStyles = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'submitted':
      return 'bg-blue-100 text-blue-800';
    case 'preparing':
      return 'bg-yellow-100 text-yellow-800';
    case 'running':
      return 'bg-green-100 text-green-800';
    case 'completed':
      return 'bg-purple-100 text-purple-800';
    case 'paused':
      return 'bg-orange-100 text-orange-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Format campaign status for display
 */
export const formatCampaignStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

/**
 * Calculate workload distribution statistics
 */
export interface WorkloadStats {
  totalCSRs: number;
  averageWorkload: number;
  minWorkload: number;
  maxWorkload: number;
  workloadVariance: number;
  balanceScore: number; // 0-100, higher is better balanced
}

export const calculateWorkloadStats = (csrWorkloads: CSRWorkload[]): WorkloadStats => {
  if (csrWorkloads.length === 0) {
    return {
      totalCSRs: 0,
      averageWorkload: 0,
      minWorkload: 0,
      maxWorkload: 0,
      workloadVariance: 0,
      balanceScore: 100
    };
  }

  const scores = csrWorkloads.map(csr => csr.total_workload_score);
  const totalCSRs = csrWorkloads.length;
  const averageWorkload = scores.reduce((sum, score) => sum + score, 0) / totalCSRs;
  const minWorkload = Math.min(...scores);
  const maxWorkload = Math.max(...scores);
  
  // Calculate variance
  const variance = scores.reduce((sum, score) => sum + Math.pow(score - averageWorkload, 2), 0) / totalCSRs;
  const standardDeviation = Math.sqrt(variance);
  
  // Calculate balance score (0-100, where 100 is perfectly balanced)
  // Lower standard deviation = higher balance score
  const maxPossibleDeviation = Math.max(averageWorkload, 10); // Prevent division by very small numbers
  const balanceScore = Math.max(0, Math.min(100, 100 - (standardDeviation / maxPossibleDeviation) * 100));

  return {
    totalCSRs,
    averageWorkload: Math.round(averageWorkload * 100) / 100,
    minWorkload,
    maxWorkload,
    workloadVariance: Math.round(variance * 100) / 100,
    balanceScore: Math.round(balanceScore)
  };
};

/**
 * Default workload threshold - can be overridden
 */
export const DEFAULT_WORKLOAD_THRESHOLD = 150;

/**
 * Validate bulk assignment request with configurable threshold
 */
export const validateBulkAssignment = (
  selectedCampaigns: string[],
  targetCSR: string,
  availableCSRs: CSRWorkload[],
  workloadThreshold: number = DEFAULT_WORKLOAD_THRESHOLD
): { isValid: boolean; error?: string; projectedWorkload?: number } => {
  if (selectedCampaigns.length === 0) {
    return { isValid: false, error: 'No campaigns selected for bulk assignment' };
  }

  if (!targetCSR) {
    return { isValid: false, error: 'No CSR selected for bulk assignment' };
  }

  const csr = availableCSRs.find(c => c.csr_id === targetCSR);
  if (!csr) {
    return { isValid: false, error: 'Selected CSR not found or not available' };
  }

  // Calculate projected workload after bulk assignment
  const projectedWorkload = csr.total_workload_score + (selectedCampaigns.length * 3);
  
  // Check against configurable threshold
  if (projectedWorkload > workloadThreshold) {
    return { 
      isValid: false, 
      error: `This assignment would result in very high workload (${projectedWorkload}) for ${csr.csr_email}. Current threshold: ${workloadThreshold}. Consider distributing across multiple CSRs or adjusting the threshold.`,
      projectedWorkload
    };
  }

  return { isValid: true, projectedWorkload };
};

/**
 * Generate a summary of assignment changes for confirmation
 */
export interface AssignmentSummary {
  totalCampaigns: number;
  targetCSR: string;
  targetCSREmail: string;
  currentWorkload: number;
  projectedWorkload: number;
  workloadIncrease: number;
  workloadThreshold: number;
  wouldExceedThreshold: boolean;
}

/**
 * Generate assignment summary with workload projection
 */
export const generateAssignmentSummary = (
  selectedCampaigns: string[],
  targetCSRId: string,
  csrWorkloads: CSRWorkload[],
  workloadThreshold: number = DEFAULT_WORKLOAD_THRESHOLD
): AssignmentSummary | null => {
  const targetCSR = csrWorkloads.find(csr => csr.csr_id === targetCSRId);
  if (!targetCSR) return null;

  const workloadIncrease = selectedCampaigns.length * 3; // Each campaign adds 3 to workload
  const projectedWorkload = targetCSR.total_workload_score + workloadIncrease;

  return {
    totalCampaigns: selectedCampaigns.length,
    targetCSR: targetCSRId,
    targetCSREmail: targetCSR.csr_email,
    currentWorkload: targetCSR.total_workload_score,
    projectedWorkload,
    workloadIncrease,
    workloadThreshold,
    wouldExceedThreshold: projectedWorkload > workloadThreshold
  };
};

/**
 * Assignment type detection and visual indicators
 */
export type AssignmentType = 'company' | 'campaign' | 'mixed' | 'unassigned';

export const getAssignmentType = (
  campaign: AdminCampaign,
  companyCSRId?: string
): AssignmentType => {
  if (!campaign.assigned_csr) return 'unassigned';
  
  // If company has assigned CSR and campaign CSR matches, it's a company assignment
  if (companyCSRId && campaign.assigned_csr === companyCSRId) {
    return 'company';
  }
  
  // Otherwise it's an individual campaign assignment
  return 'campaign';
};

/**
 * Get CSS classes for assignment type indicators
 */
export const getAssignmentTypeStyles = (type: AssignmentType): string => {
  switch (type) {
    case 'company':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'campaign':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'mixed':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'unassigned':
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

/**
 * Get assignment type label for display
 */
export const getAssignmentTypeLabel = (type: AssignmentType): string => {
  switch (type) {
    case 'company':
      return '🏢 Company';
    case 'campaign':
      return '📋 Campaign';
    case 'mixed':
      return '🔀 Mixed';
    case 'unassigned':
    default:
      return 'Unassigned';
  }
};

/**
 * Determine CSR assignment type based on workload data
 */
export const getCSRAssignmentType = (workload: EnhancedCSRWorkload): AssignmentType => {
  if (workload.assigned_companies > 0 && workload.assigned_campaigns > 0) {
    return 'mixed';
  } else if (workload.assigned_companies > 0) {
    return 'company';
  } else if (workload.assigned_campaigns > 0) {
    return 'campaign';
  }
  return 'unassigned';
};

/**
 * Enhanced workload statistics calculation with company assignments
 */
export interface EnhancedWorkloadStats extends WorkloadStats {
  totalCompanyAssignments: number;
  averageCompaniesPerCSR: number;
  companyWorkloadVariance: number;
}

export const calculateEnhancedWorkloadStats = (csrWorkloads: EnhancedCSRWorkload[]): EnhancedWorkloadStats => {
  const basicStats = calculateWorkloadStats(csrWorkloads);
  
  if (csrWorkloads.length === 0) {
    return {
      ...basicStats,
      totalCompanyAssignments: 0,
      averageCompaniesPerCSR: 0,
      companyWorkloadVariance: 0
    };
  }

  const companyAssignments = csrWorkloads.map(csr => csr.assigned_companies);
  const totalCompanyAssignments = companyAssignments.reduce((sum, count) => sum + count, 0);
  const averageCompaniesPerCSR = totalCompanyAssignments / csrWorkloads.length;
  
  const companyVariance = companyAssignments.reduce((sum, count) => 
    sum + Math.pow(count - averageCompaniesPerCSR, 2), 0
  ) / csrWorkloads.length;

  return {
    ...basicStats,
    totalCompanyAssignments,
    averageCompaniesPerCSR: Math.round(averageCompaniesPerCSR * 100) / 100,
    companyWorkloadVariance: Math.round(companyVariance * 100) / 100
  };
};

/**
 * Debounce function for search input
 */
export const debounce = <T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};